import { useEffect, useMemo, useRef, useState } from "react"
import type { Socket } from "socket.io-client"

export type SocketConnectionStatus =
  | "connecting"
  | "connected"
  | "disconnected"
  | "error"

export type OrderUpdatedPayload = {
  event?: string
  data?: {
    id: string
    display_id?: number
    status?: string
    fulfillment_status?: string
    created_at?: string
  }
}

export type NotificationNewPayload = {
  id: string
  type: string
  title: string
  content: string | null
  action_url: string | null
  data: Record<string, unknown> | null
  created_at: string
}

export interface UseSocketDashboardReturn {
  connectionStatus: SocketConnectionStatus
  isConnected: boolean
  lastOrderEvent: OrderUpdatedPayload | null
  lastNotificationEvent: NotificationNewPayload | null
}

const getSocketUrl = () => {
  const explicitSocketUrl = import.meta.env.VITE_MEDUSA_ADMIN_SOCKET_URL
  if (explicitSocketUrl) {
    return explicitSocketUrl
  }

  const backendUrl =
    (typeof window !== "undefined" && (window as any).__BACKEND_URL__) ||
    import.meta.env.VITE_MEDUSA_BACKEND_URL ||
    "http://localhost:9000"

  try {
    const url = new URL(backendUrl)
    if (url.protocol === "https:") {
      url.port = ""
    } else {
      url.port = "9001"
    }

    return url.origin
  } catch {
    return "http://localhost:9001"
  }
}

export function useSocketDashboard(): UseSocketDashboardReturn {
  const [connectionStatus, setConnectionStatus] =
    useState<SocketConnectionStatus>("disconnected")
  const [lastOrderEvent, setLastOrderEvent] =
    useState<OrderUpdatedPayload | null>(null)
  const [lastNotificationEvent, setLastNotificationEvent] =
    useState<NotificationNewPayload | null>(null)

  const socketRef = useRef<Socket | null>(null)

  const backendUrl = useMemo(() => {
    return (
      (typeof window !== "undefined" && (window as any).__BACKEND_URL__) ||
      import.meta.env.VITE_MEDUSA_BACKEND_URL ||
      "http://localhost:9000"
    )
  }, [])

  useEffect(() => {
    const SOCKET_URL = getSocketUrl()

    let isCancelled = false

    ;(async () => {
      try {
        setConnectionStatus("connecting")

        const tokenRes = await fetch(
          `${backendUrl.replace(/\/$/, "")}/admin/socket/token`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "content-type": "application/json",
            },
          }
        )

        const tokenData = (await tokenRes.json().catch(() => ({}))) as {
          token?: string | null
        }

        const token = tokenData?.token ?? undefined
        if (!token) {
          if (!isCancelled) setConnectionStatus("error")
          return
        }

        if (isCancelled) return

        const { io } = await import("socket.io-client")

        const socket = io(SOCKET_URL, {
          transports: ["websocket", "polling"],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          auth: {
            token,
          },
        })

        socketRef.current = socket

        socket.on("connect", () => {
          setConnectionStatus("connected")
        })

        socket.on("connecting", () => {
          setConnectionStatus("connecting")
        })

        socket.on("disconnect", () => {
          setConnectionStatus("disconnected")
        })

        socket.on("connect_error", () => {
          setConnectionStatus("error")
        })

        socket.on("order_updated", (payload: OrderUpdatedPayload) => {
          setLastOrderEvent(payload)
        })

        socket.on("notification:new", (payload: NotificationNewPayload) => {
          setLastNotificationEvent(payload)
        })
      } catch {
        if (!isCancelled) setConnectionStatus("error")
      }
    })()

    return () => {
      isCancelled = true
      socketRef.current?.disconnect()
      socketRef.current = null
    }
  }, [backendUrl])

  return {
    connectionStatus,
    isConnected: connectionStatus === "connected",
    lastOrderEvent,
    lastNotificationEvent,
  }
}

export default useSocketDashboard
